import { Box } from '@mui/material';
import { compact } from 'lodash';
import { useEffect, useMemo, useRef } from 'react';

import {
  DebugLevel,
  useDebugContext,
} from '@/contexts/debugContext/debug.context';
import { useHouseholdDocumentsViewerContext } from '@/modules/documents/components/HouseholdDocumentsViewer/context/HouseholdDocumentsViewer.context';
import { DocumentSearchResult } from '@/modules/pdf/DocumentSearch/DocumentSearch.types';
import { zIndexes } from '@/styles/zIndexes';
import { Coordinate } from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';

import { useAnnotatedPDFViewerContext } from './AnnotatedPDFViewer/context/AnnotatedPDFViewer.context';
import { getScrollParent } from './Annotations.utils';
import { DebugAnnotationBox } from './debug/DebugAnnotationBox';
import { DebugAnnotationInfo } from './debug/DebugAnnotationInfo';
import { usePDFViewerColors } from './usePDFViewerColors';

interface AnnotationProps {
  // annotationGeometry is an array of points that make up the polygon, we need at least two points
  // to draw the bounding rectangle
  annotationGeometry: Coordinate[];
  annotationIdx: number;
  isFocused: boolean;
  annotationDocumentId: string;
  canvasElement: HTMLCanvasElement;
  pageViewport: {
    width: number;
    height: number;
  };
  pageOffset: number;
  zoomLevel?: number; // Add zoom level to trigger re-scroll when zoom changes
  scrollToActive: boolean;
  /** Draws the annotation with the active colors without scrolling to it. */
  isPrimary: boolean;
  highlightColors?: AnnotationHighlightColors;
}

/**
 * Replaces the colors derived from the page. Primary annotations use the
 * active colors; other annotations use the lighter ones.
 */
export interface AnnotationHighlightColors {
  activeBackground: string;
  background: string;
  activeBorder: string;
  border: string;
}

export const ANNOTATION_ID_PREFIX = 'annotation-';
const ANNOTATION_PADDING_PX = 2;
const ANNOTATION_SCROLL_OFFSET_PX = 200;

function Annotation({
  annotationGeometry,
  annotationIdx,
  isFocused,
  annotationDocumentId,
  canvasElement,
  pageViewport,
  pageOffset,
  zoomLevel,
  scrollToActive,
  isPrimary,
  highlightColors,
}: AnnotationProps) {
  const ref = useRef<HTMLDivElement>(null);
  // Read through a ref so that a change of this flag alone does not scroll.
  const scrollToActiveRef = useRef(scrollToActive);
  scrollToActiveRef.current = scrollToActive;
  const { debugLevel } = useDebugContext();
  const { activeDocumentId } = useHouseholdDocumentsViewerContext();
  const { documentId } = useAnnotatedPDFViewerContext();

  const documentIdForAnnotation = activeDocumentId ?? documentId;

  useEffect(() => {
    if (isFocused && ref.current && scrollToActiveRef.current) {
      const scrollParent = getScrollParent(ref.current);

      const scrollPosition =
        ref.current.offsetTop -
        scrollParent.getBoundingClientRect().top -
        ANNOTATION_SCROLL_OFFSET_PX;
      scrollParent.scrollTo({
        top: scrollPosition,
        behavior: 'smooth',
      });
    }
  }, [isFocused, annotationIdx, zoomLevel]); // Add zoomLevel to re-scroll when zoom changes

  const pageColors = usePDFViewerColors({
    element: canvasElement,
    isFocused,
  });
  let { backgroundColor, borderColor } = pageColors;
  if (highlightColors) {
    backgroundColor = isPrimary
      ? highlightColors.activeBackground
      : highlightColors.background;
    borderColor = isPrimary
      ? highlightColors.activeBorder
      : highlightColors.border;
  }

  // Raw coordinates without padding
  const rawLeft = Math.min(...annotationGeometry.map((a) => a.x));
  const rawRight = Math.max(...annotationGeometry.map((a) => a.x));
  const rawTop = Math.min(...annotationGeometry.map((a) => a.y)) + pageOffset;
  const rawBottom =
    Math.max(...annotationGeometry.map((a) => a.y)) + pageOffset;
  const rawWidth = rawRight - rawLeft;
  const rawHeight = rawBottom - rawTop;

  // Padded coordinates
  const paddedLeft = rawLeft - ANNOTATION_PADDING_PX;
  const paddedTop = rawTop - ANNOTATION_PADDING_PX;
  const paddedWidth = rawWidth + ANNOTATION_PADDING_PX * 2;
  const paddedHeight = rawHeight + ANNOTATION_PADDING_PX * 2;

  if (
    documentIdForAnnotation &&
    documentIdForAnnotation !== annotationDocumentId
  ) {
    // Only show the annotation if it's on the active document.
    // This prevents us from showing outlines of annotations of other documents,
    // which causes the annotation to look "wrong" because it doesn't line up
    // with a paragraph of the active doc.
    return null;
  }

  return (
    <>
      {debugLevel === DebugLevel.ENHANCED && (
        <DebugAnnotationBox
          left={rawLeft}
          top={rawTop}
          width={rawWidth}
          height={rawHeight}
        />
      )}

      <Box
        id={`${ANNOTATION_ID_PREFIX}${annotationIdx}`}
        ref={ref}
        data-testid={`PDFViewerAnnotation-${annotationIdx}`}
        sx={{
          position: 'absolute',
          left: paddedLeft + 'px',
          width: paddedWidth + 'px',
          top: paddedTop + 'px',
          height: paddedHeight + 'px',
          backgroundColor,
          mixBlendMode: 'multiply',
          cursor: 'pointer',
          zIndex: zIndexes.HIGHLIGHT,
          borderRadius: '6px',
          border: `2px dashed ${borderColor}`,
        }}
      >
        {debugLevel === DebugLevel.ENHANCED && (
          <DebugAnnotationInfo
            left={paddedLeft}
            top={paddedTop}
            width={paddedWidth}
            height={paddedHeight}
            originalCoords={annotationGeometry}
            pageOffset={pageOffset}
            viewport={pageViewport}
          />
        )}
      </Box>
    </>
  );
}

interface AnnotationsProps {
  // Per-page viewport dimensions (one for each page)
  pageViewports: Array<{
    width: number;
    height: number;
  }>;
  canvasElement: HTMLCanvasElement;
  annotations: DocumentSearchResult[];
  activeAnnotationIdx: number;
  pageOffsets: number[];
  /** One-based page numbers currently visible in the viewer. */
  visiblePageNumbers?: number[];
  filePages: {
    pageNumber: number;
    widthInches?: number | null;
    heightInches?: number | null;
  }[];
  zoomLevel?: number; // Add zoom level to trigger re-scroll when zoom changes
  /**
   * When false, the active annotation is highlighted but the viewer does not
   * scroll to it. Defaults to true.
   */
  scrollToActive?: boolean;
  /** Fixed colors for hosts that coordinate annotations with other overlays. */
  highlightColors?: AnnotationHighlightColors;
  /**
   * Number of annotations, starting at the active one, that use the active
   * colors. They belong to the same reference. Defaults to 1.
   */
  primaryAnnotationCount?: number;
}

export function Annotations({
  pageViewports,
  canvasElement,
  annotations,
  activeAnnotationIdx,
  pageOffsets,
  filePages,
  zoomLevel,
  scrollToActive = true,
  highlightColors,
  primaryAnnotationCount = 1,
}: AnnotationsProps) {
  const annotationData = useMemo(() => {
    const polygons = compact(annotations?.map((e) => e.Polygon) ?? []);

    return polygons.map((annotation, idx) => {
      const pageNumber = annotations[idx]?.PageNumber ?? 1;
      const filePage = filePages[pageNumber - 1];
      const pageWidth = filePage?.widthInches ?? 8.5;
      const pageHeight = filePage?.heightInches ?? 11;

      // Use the viewport for this specific page, not a global viewport
      const pageViewport = pageViewports[pageNumber - 1];
      if (!pageViewport) {
        diagnostics.warn(`No viewport found for page ${pageNumber}`);
        return [];
      }

      return annotation.map((a) => {
        return {
          x: a.x * (pageViewport.width / pageWidth),
          y: a.y * (pageViewport.height / pageHeight),
        };
      });
    });
  }, [annotations, pageViewports, filePages]);

  return (
    <>
      {annotationData.map((annotationGeometry, idx) => {
        const pageNumber = annotations[idx]?.PageNumber ?? 1;
        const offset = pageOffsets[pageNumber - 1];
        if (typeof offset !== 'number') {
          diagnostics.warn(`No page offset found for page ${pageNumber}`);
          return null;
        }

        const pageViewport = pageViewports[pageNumber - 1];
        if (!pageViewport) {
          diagnostics.warn(
            `No viewport found for page ${pageNumber} when rendering annotation`
          );
          return null;
        }

        return (
          <Annotation
            key={`annotation-${idx}`}
            annotationGeometry={annotationGeometry}
            annotationIdx={idx}
            isFocused={idx === activeAnnotationIdx}
            isPrimary={
              // A negative index means no citation is active, such as while
              // the user adds a reference.
              activeAnnotationIdx >= 0 &&
              idx >= activeAnnotationIdx &&
              idx < activeAnnotationIdx + primaryAnnotationCount
            }
            annotationDocumentId={annotations[idx]?.documentID ?? ''}
            canvasElement={canvasElement}
            pageViewport={pageViewport}
            pageOffset={offset}
            zoomLevel={zoomLevel}
            scrollToActive={scrollToActive}
            highlightColors={highlightColors}
          />
        );
      })}
    </>
  );
}
